// What the order screens are drawn with: the state chips, money and dates.
import React from "react"
import { twMerge } from "tailwind-merge"
import { formatMoney } from "aldoo-ra/Store/currency"

export { Thumb } from "aldoo-ra/StoreSales/ui"

const NEUTRAL = "bg-admin_neutral_1 text-admin_text_secondary"
const SUCCESS = "bg-admin_success/10 text-admin_success"
const WARNING = "bg-admin_warning/10 text-admin_warning"
const ERROR = "bg-admin_error/10 text-admin_error"
const INFO = "bg-admin_info/10 text-admin_info"
const RETURNED = "bg-[#6b5ca5]/10 text-[#6b5ca5]"
// processing: placed to be paid on delivery, not sent yet — open, as a paid one
const ORDER_STATUS = { paid: INFO, processing: INFO, shipping: WARNING, completed: SUCCESS, canceled: ERROR, refunded: ERROR, returned: RETURNED }

/**
 * The one state an order is in. It carries two fields — how far it got
 * (`orderStatus`) and what happened to the money (`status`, which turns to
 * `refunded`). Money all given back ends the order: it says refunded. A part
 * of it does not — what is left still goes out, so the order says how far it
 * got (open, shipping, completed); the refund is in its own details.
 */
export const orderState = (order) => {
  // the goods came back: said whatever the money did (a refund is shown beside it)
  if (order?.orderStatus === "returned") return { label: "returned", className: RETURNED }
  const refunded = order?.refundedAmount ?? 0
  if (order?.status === "refunded" || (refunded > 0 && refunded >= (order?.amount ?? 0))) return { label: "refunded", className: ERROR }
  const status = order?.orderStatus || order?.status || "—"
  // "paid" is how it is stored; "open" is what it means to whoever has to send it — and an order to
  // be paid on delivery ("processing") waits to be sent: nobody is working on it yet
  return { label: status === "paid" ? "open" : status === "processing" ? "awaiting shipment" : status, className: ORDER_STATUS[status] || NEUTRAL }
}

// every chip reads the same way: its first letter capitalized ("Partly refunded", "Чака изпращане"),
// whatever case its words come in — not each word, which is wrong in Bulgarian ("Обработва Се")
export const Chip = ({ children, className = "" }) => (
  <span className={twMerge("inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap", className)}>
    {typeof children === "string" ? <span className="inline-block first-letter:uppercase">{children}</span> : children}
  </span>
)

export const Empty = ({ children }) => <div className="text-sm text-admin_text_secondary italic py-6 text-center">{children}</div>

export const Section = ({ title, actions, children }) => (
  <section className="bg-admin_paper border border-admin_bordered rounded-lg p-4 flex flex-col gap-3">
    {(title || actions) && (
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        {title && <h3 className="my-0 text-lg font-semibold text-admin_text">{title}</h3>}
        {actions}
      </div>
    )}
    {children}
  </section>
)

export const money = (cents, currency) => {
  if (cents === null || cents === undefined) return "-"
  return formatMoney(cents, currency && String(currency).toUpperCase(), { cents: true, locale: "en" })
}

/** The date and time; given the screen's t, how long ago follows it: "29 Sept 2026, 16:18 (4 days ago)". */
export const dateTime = (d, locale = "en-GB", t = null) => {
  if (!d) return "-"
  const text = new Date(d).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
  return t ? `${text} (${timeAgo(d, t)})` : text
}
/**
 * When the order was placed. A paid order counts from its payment (completedAt: the transaction is made when the
 * checkout opens, which can be a day before); one paid on delivery is placed when its transaction is made, its
 * completedAt being the day the courier collected the money.
 */
export const placedAt = (order) => (order?.paymentMethod?.type === "cash_on_delivery" ? order.createdAt : order?.completedAt || order?.createdAt)
export const dateOnly = (d, locale = "en-GB") => (d ? new Date(d).toLocaleDateString(locale, { dateStyle: "medium" }) : "-")

/** How long ago, in the largest unit that still says something. */
export const timeAgo = (d, t) => {
  if (!d) return ""
  const seconds = Math.round((Date.now() - new Date(d).getTime()) / 1000)
  if (!Number.isFinite(seconds)) return ""
  if (seconds < 60) return t("just now")
  const units = [
    { limit: 3600, size: 60, one: "%sm ago", many: "%sm ago" },
    { limit: 86400, size: 3600, one: "%sh ago", many: "%sh ago" },
    { limit: 604800, size: 86400, one: "%s day ago", many: "%s days ago" },
    { limit: 2629800, size: 604800, one: "%s week ago", many: "%s weeks ago" },
    { limit: 31557600, size: 2629800, one: "%s month ago", many: "%s months ago" },
  ]
  for (const { limit, size, one, many } of units) {
    if (seconds >= limit) continue
    const count = Math.floor(seconds / size)
    return t(count === 1 ? one : many, count)
  }
  const years = Math.floor(seconds / 31557600)
  return t(years === 1 ? "%s year ago" : "%s years ago", years)
}

/**
 * The order's pre-sale lines (a product sold before it was out: `line.presale`, as checkout kept the
 * promise), or null: how many, and when the order can go — the last of them released, the last
 * delivery promised.
 */
export const presaleOf = (order) => {
  const lines = (order?.cart || []).filter((line) => line?.presale?.releaseDate)
  if (!lines.length) return null
  const latest = (field) => lines.map((line) => line.presale[field]).filter(Boolean).sort((a, b) => new Date(b) - new Date(a))[0] || null
  return { count: lines.length, releaseDate: latest("releaseDate"), deliverBy: latest("deliverBy") }
}
/** Not sent yet: paid, or placed to be paid on delivery — what a pre-sale notice is there for. */
export const awaitingShipment = (order) => order?.orderStatus === "paid" || order?.orderStatus === "processing"
// a pre-sale's chip, wherever it is said
export const PRESALE = "bg-[#7c3aed]/10 text-[#7c3aed]"

/** What went wrong, from an api answer ("" when nothing did). */
export const errorMessage = (res, fallback = "Request failed") => {
  if (res === "") return ""
  if (!res) return fallback
  if (typeof res.error === "string") return res.message || res.error
  if (res.error) return res.message || fallback
  return ""
}

// a buyer's reason to cancel an order (aldoo-na/store/cancellations REASONS), as the seller reads it
export const CANCEL_REASON_LABELS = {
  ordered_by_mistake: "Ordered by mistake",
  changed_mind: "Changed their mind",
  found_cheaper: "Found it cheaper elsewhere",
  delivery_too_slow: "Delivery takes too long",
  other: "Another reason",
}
